"use client";

import { areCurrenciesEqual, ContextCurrencyFragment, readCurrencyFragment, useCurrencies } from "@opensea/app-kit";
import { graphql, readFragment } from "@opensea/graphql";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
export const useSwapCurrenciesFragment = graphql(`
    fragment useSwapCurrencies on SwapRouteResult {
      ... on SwapRoute {
        fromAsset {
          ...ContextCurrency
        }
        toAsset {
          ...ContextCurrency
        }
      }
    }
  `, [ContextCurrencyFragment]);
export const useSwapCurrencies = ({
  swapRoute: swapRouteKey
}) => {
  const swapRoute = readFragment(useSwapCurrenciesFragment, swapRouteKey);
  const {
    control
  } = useFormContext();
  const [fromCurrencyIdentifier, toCurrencyIdentifier] = useWatch({
    control,
    name: ["fromCurrency", "toCurrency"]
  });

  // Use local currencies (when available) if the swap route is not loaded yet
  const {
    getCurrency
  } = useCurrencies();

  // Keep last currencies in state to use while new swap quote is loading for same currencies
  const [lastToAsset, setLastToAsset] = useState(undefined);
  const [lastFromAsset, setLastFromAsset] = useState(undefined);
  useEffect(() => {
    if (swapRoute?.__typename !== "SwapRoute") {
      return;
    }
    if (swapRoute.toAsset.__typename === "Currency") {
      setLastToAsset(readCurrencyFragment(swapRoute.toAsset));
    }
    if (swapRoute.fromAsset.__typename === "Currency") {
      setLastFromAsset(readCurrencyFragment(swapRoute.fromAsset));
    }
  }, [swapRoute]);
  const toAsset = useMemo(() => {
    if (swapRoute?.__typename === "SwapRoute" && swapRoute.toAsset.__typename === "Currency") {
      return readCurrencyFragment(swapRoute.toAsset);
    }
    if (lastToAsset && toCurrencyIdentifier && areCurrenciesEqual(lastToAsset, toCurrencyIdentifier)) {
      return lastToAsset;
    }
    return toCurrencyIdentifier ? getCurrency(toCurrencyIdentifier) : undefined;
  }, [lastToAsset, toCurrencyIdentifier, getCurrency, swapRoute]);
  const fromAsset = useMemo(() => {
    if (swapRoute?.__typename === "SwapRoute" && swapRoute.fromAsset.__typename === "Currency") {
      return readCurrencyFragment(swapRoute.fromAsset);
    }
    if (lastFromAsset && areCurrenciesEqual(lastFromAsset, fromCurrencyIdentifier)) {
      return lastFromAsset;
    }
    return getCurrency(fromCurrencyIdentifier);
  }, [lastFromAsset, fromCurrencyIdentifier, getCurrency, swapRoute]);
  return {
    fromAsset,
    toAsset
  };
};